import React, { useState, useMemo } from 'react';
import {
  Autocomplete,
  TextField,
  FormControl,
  Grid,
  Box,
  useTheme
} from '@mui/material';
import { Agency } from '../utils/types';
import { useDebounce } from '../hooks/useDebounce';

interface SearchableAgencyDropdownProps {
  agencies: Agency[];
  onSelect: (agencyId: number | string) => void;
  selectedAgencyId?: number | string | null;
  loading?: boolean;
  icon?: React.ReactNode;
  label?: string;
}

const SearchableAgencyDropdown: React.FC<SearchableAgencyDropdownProps> = ({
  agencies,
  onSelect,
  selectedAgencyId,
  loading = false,
  icon,
  label = 'Agency'
}) => {
  const theme = useTheme();
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const allAgenciesOption: Agency = useMemo(() => ({ id: 'all', name: 'All Agencies' }), []);

  // Debounced search to improve performance
  const debouncedSearch = useDebounce((term: string) => {
    setSearchTerm(term);
  }, 300);

  // Find the currently selected option (either All Agencies or a specific agency)
  const selectedOption = useMemo(() => {
    if (selectedAgencyId === 'all' || selectedAgencyId === null) return allAgenciesOption;
    return agencies.find(agency => agency.id === selectedAgencyId) || null;
  }, [agencies, selectedAgencyId, allAgenciesOption]);

  // Combine "All Agencies" option with individual agencies and apply search filter to the entire list
  const filteredAgencies = useMemo(() => {
    const searchResults = [allAgenciesOption, ...agencies];

    if (!searchTerm.trim()) {
      return searchResults;
    }
    
    const lowercaseSearch = searchTerm.toLowerCase();
    return searchResults.filter(agency =>
      agency.name.toLowerCase().includes(lowercaseSearch)
    );
  }, [allAgenciesOption, agencies, searchTerm]);

  const handleInputChange = (_event: React.SyntheticEvent, value: string) => {
    debouncedSearch(value);
  };

  const handleSelectionChange = (_event: React.SyntheticEvent, value: Agency | null) => {
    if (value) {
      onSelect(value.id);
    }
  };

  const handleOpen = () => {
    setIsOpen(true);
    setSearchTerm('');
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  return (
    <Grid size={3} container>
      <Grid
        size={3}
        sx={{
          padding: 1,
          gap: 2,
          display: 'flex',
          alignItems: 'center',
          backgroundColor: theme.palette.blueGrey[50],
          borderRadius: '8px',
          width: '100%'
        }}>
        {icon && (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {icon}
          </Box>
        )}
        <FormControl
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'flex-start',
            width: '100%'
          }}
        >
          <Autocomplete
            options={filteredAgencies}
            value={selectedOption}
            loading={loading}
            open={isOpen}
            onOpen={handleOpen}
            onClose={handleClose}
            getOptionLabel={(option) => option.name}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            onChange={handleSelectionChange}
            onInputChange={handleInputChange}
            filterOptions={(x) => x} // Disable built-in filtering since we handle it ourselves
            noOptionsText="No agencies found"
            renderInput={(params) => (
              <TextField
                {...params}
                label={label}
                variant="outlined"
                size="small"
                slotProps={{
                  ...params.slotProps,

                  inputLabel: {
                    ...params.slotProps.inputLabel,
                    shrink: true,
                    sx: {
                      position: 'static',
                      transform: 'none',
                      fontSize: '8pt',
                      '&.MuiInputLabel-outlined.Mui-focused': { 
                        color: theme.palette.primary.dark 
                      }
                    }
                  },

                  htmlInput: {
                    ...params.slotProps.htmlInput,
                    'data-testid': 'searchable-agency-dropdown'
                  }
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    border: 'none',
                    '& fieldset': {
                      border: 'none'
                    },
                    '&:hover fieldset': {
                      border: 'none'
                    },
                    '&.Mui-focused fieldset': {
                      border: 'none'
                    }
                  },
                  '& .MuiInputBase-input': {
                    padding: '4px 0',
                    fontSize: '14px'
                  },
                  width: '100%'
                }}
              />
            )}
            renderOption={(props, option) => (
              <Box component="li" {...props} key={option.id}>
                {option.name}
              </Box>
            )}
            sx={{
              width: '100%',
              '& .MuiAutocomplete-endAdornment': {
                top: '50%',
                transform: 'translateY(-50%)'
              }
            }}
          />
        </FormControl>
      </Grid>
    </Grid>
  );
};

export default SearchableAgencyDropdown;